import React, { useState, useRef, useEffect } from 'react';
import styled from 'styled-components';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'react-toastify';
import { filterClients } from '../../utils/clientSearch';
import apiService, { api } from '../../services/api';
import { useAuth } from '../../contexts/AuthContext';
import { clientDiscountSuffix, mostrarErrorAltaCliente, avisarAltaCliente } from '../../utils/clientDisplay';

const SelectContainer = styled.div`
  position: relative;
  width: 100%;
`;

const SearchInput = styled.input`
  width: 100%;
  padding: 0.75rem;
  border: 1px solid #ddd;
  border-radius: 4px;
  font-size: 1rem;
  transition: border-color 0.3s;

  &:focus {
    outline: none;
    border-color: #4CAF50;
  }

  &:disabled {
    background-color: #f5f5f5;
  }
`;

const DropdownList = styled.div`
  position: absolute;
  top: 100%;
  left: 0;
  right: 0;
  background: white;
  border: 1px solid #ddd;
  border-top: none;
  border-radius: 0 0 4px 4px;
  max-height: 350px;
  overflow: hidden;
  display: flex;
  flex-direction: column;
  z-index: 1000;
  box-shadow: 0 4px 8px rgba(0, 0, 0, 0.1);
`;

const DropdownScrollArea = styled.div`
  flex: 1;
  overflow-y: auto;
  max-height: 280px;
`;

const DropdownItem = styled.div`
  padding: 0.75rem;
  cursor: pointer;
  transition: background 0.2s;
  border-bottom: 1px solid #f0f0f0;

  &:hover {
    background: #f8f9fa;
  }

  &:last-child {
    border-bottom: none;
  }
`;

const ClientName = styled.div`
  font-weight: 500;
  color: #333;
`;

const ClientDetails = styled.div`
  font-size: 0.85rem;
  color: #666;
  margin-top: 0.25rem;
`;

const CreateOption = styled.div`
  padding: 0.75rem;
  cursor: pointer;
  background: #e8f5e9;
  color: #2e7d32;
  font-weight: 600;
  border-top: 2px solid #c8e6c9;
  flex-shrink: 0;

  &:hover {
    background: #c8e6c9;
  }
`;

const Modal = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 2000;
`;

const ModalContent = styled.div`
  background: white;
  border-radius: 8px;
  padding: 1.5rem;
  width: 95%;
  max-width: 700px;
  max-height: 90vh;
  overflow-y: auto;
`;

const ModalTitle = styled.h2`
  margin: 0 0 1.25rem 0;
  color: #333;
  font-size: 1.3rem;
`;

const Form = styled.form`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 1rem;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`;

const FormGroup = styled.div`
  display: flex;
  flex-direction: column;
`;

const FormRow = styled.div`
  grid-column: span 2;
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 1rem;

  @media (max-width: 768px) {
    grid-column: span 1;
    grid-template-columns: 1fr;
  }
`;

const FullWidth = styled.div`
  grid-column: span 2;

  @media (max-width: 768px) {
    grid-column: span 1;
  }
`;

const Label = styled.label`
  font-weight: 500;
  margin-bottom: 0.3rem;
  color: #555;
  font-size: 0.9rem;
`;

const Input = styled.input`
  padding: 0.6rem;
  border: 1px solid #ddd;
  border-radius: 4px;
  font-size: 0.95rem;

  &:focus {
    outline: none;
    border-color: #4CAF50;
  }
`;

const ButtonGroup = styled.div`
  display: flex;
  gap: 0.75rem;
  justify-content: flex-end;
  margin-top: 0.75rem;
`;

const Button = styled.button`
  padding: 0.6rem 1.25rem;
  border: none;
  border-radius: 4px;
  font-size: 1rem;
  cursor: pointer;
  transition: background-color 0.3s;

  &.primary {
    background-color: #4CAF50;
    color: white;

    &:hover {
      background-color: #45a049;
    }

    &:disabled {
      background-color: #ccc;
      cursor: not-allowed;
    }
  }

  &.secondary {
    background-color: #f5f5f5;
    color: #333;

    &:hover {
      background-color: #e0e0e0;
    }
  }
`;

const ClientSelectWithCreate = ({ value, onChange, onClientCreated, disabled = false, placeholder = "Buscar o crear cliente...", fetchAllClients = false }) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [showDropdown, setShowDropdown] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [displayValue, setDisplayValue] = useState('');
  const containerRef = useRef(null);
  const queryClient = useQueryClient();

  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    street: '',
    street2: '',
    city: '',
    zip: '',
    taxId: '',
    marginPercentage: 0
  });

  // Usar endpoint diferente según si necesitamos todos los clientes o solo profesionales
  const { data: clientsData, isLoading } = useQuery({
    queryKey: fetchAllClients ? ['allClients'] : ['clients'],
    queryFn: fetchAllClients
      ? async () => {
          const response = await api.get('/clients');
          return response.data;
        }
      : apiService.getClients,
  });

  const clients = clientsData?.clients || (Array.isArray(clientsData) ? clientsData : []);
  const { isAdmin } = useAuth();

  // Update display value when value changes
  useEffect(() => {
    if (value && clients.length > 0) {
      const selectedClient = clients.find(c => c.id === value);
      if (selectedClient) {
        setDisplayValue(selectedClient.name);
        setSearchTerm(selectedClient.name);
      }
    }
  }, [value, clients]);

  // Búsqueda tolerante, igual que en la pantalla de Clientes: ignora mayúsculas,
  // acentos y la ñ (solo al comparar), recorta espacios sobrantes y busca por
  // teléfono comparando solo dígitos. Es el punto donde el operario, si no
  // encuentra al cliente, lo crea otra vez.
  const filteredClients = filterClients(clients, searchTerm, ['name', 'email']);

  const createClientMutation = useMutation({
    mutationFn: apiService.createClient,
    onSuccess: (data) => {
      queryClient.invalidateQueries(['clients']);
      const newClient = data.client || data;
      // "Creado", o aviso amarillo si el backend reutilizó una ficha existente (mismo teléfono/nombre)
      avisarAltaCliente(toast, newClient);

      // Select the newly created client
      onChange(newClient.id);
      setDisplayValue(newClient.name);
      setSearchTerm(newClient.name);

      setShowModal(false);
      resetForm();

      if (onClientCreated) {
        onClientCreated(newClient);
      }
    },
    onError: (error) => {
      mostrarErrorAltaCliente(toast, error, 'Error al crear el cliente: ');
    }
  });

  const resetForm = () => {
    setFormData({
      name: '',
      email: '',
      phone: '',
      street: '',
      street2: '',
      city: '',
      zip: '',
      taxId: '',
      marginPercentage: 0
    });
  };

  const handleInputChange = (e) => {
    const term = e.target.value;
    setSearchTerm(term);
    setShowDropdown(true);

    // If user clears the input, clear the selection
    if (!term) {
      onChange('');
      setDisplayValue('');
    }
  };

  const handleSelectClient = (client) => {
    onChange(client.id);
    setDisplayValue(client.name);
    setSearchTerm(client.name);
    setShowDropdown(false);
  };

  const handleCreateNew = () => {
    setFormData(prev => ({
      ...prev,
      name: searchTerm // Pre-fill with search term
    }));
    setShowModal(true);
    setShowDropdown(false);
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    const payload = {
      ...formData,
      name: formData.name?.trim() || '',
      email: formData.email?.trim() || '',
      phone: formData.phone?.trim() || '',
      street: formData.street?.trim() || '',
      street2: formData.street2?.trim() || '',
      city: formData.city?.trim() || '',
      zip: formData.zip?.trim() || '',
      taxId: formData.taxId?.trim() || ''
    };

    if (!payload.phone) {
      toast.error('El teléfono es obligatorio para crear un cliente');
      return;
    }

    createClientMutation.mutate(payload);
  };

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (containerRef.current && !containerRef.current.contains(event.target)) {
        setShowDropdown(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  return (
    <>
      <SelectContainer ref={containerRef}>
        <SearchInput
          type="text"
          value={searchTerm}
          onChange={handleInputChange}
          onFocus={() => setShowDropdown(true)}
          placeholder={placeholder}
          disabled={disabled}
        />

        {showDropdown && !disabled && (
          <DropdownList>
            {isLoading ? (
              <DropdownScrollArea>
                <DropdownItem>Cargando clientes...</DropdownItem>
              </DropdownScrollArea>
            ) : (
              <>
                <DropdownScrollArea>
                  {filteredClients.length > 0 ? (
                    filteredClients.map(client => (
                      <DropdownItem
                        key={client.id}
                        onClick={() => handleSelectClient(client)}
                      >
                        <ClientName>{client.name}{clientDiscountSuffix(client, isAdmin)}{client.odooArchived ? ' ⚠ archivado en Odoo' : ''}</ClientName>
                        <ClientDetails>
                          {client.email && <span>{client.email} • </span>}
                          {client.phone && <span>{client.phone} • </span>}
                          <span>Margen: {client.marginPercentage}%</span>
                        </ClientDetails>
                      </DropdownItem>
                    ))
                  ) : (
                    !searchTerm && (
                      <DropdownItem style={{ color: '#999', fontStyle: 'italic' }}>
                        Escribe para buscar clientes...
                      </DropdownItem>
                    )
                  )}
                </DropdownScrollArea>
                {/* Crear nuevo cliente - siempre visible al final */}
                <CreateOption onClick={handleCreateNew}>
                  ➕ Crear nuevo cliente{searchTerm ? ` "${searchTerm}"` : ''}
                </CreateOption>
              </>
            )}
          </DropdownList>
        )}
      </SelectContainer>

      {showModal && (
        <Modal onClick={() => setShowModal(false)}>
          <ModalContent onClick={(e) => e.stopPropagation()}>
            <ModalTitle>Crear Nuevo Cliente</ModalTitle>
            <Form onSubmit={handleSubmit}>
              {/* Fila 1: Nombre | Teléfono */}
              <FormGroup>
                <Label htmlFor="name">Nombre *</Label>
                <Input
                  id="name"
                  type="text"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  required
                />
              </FormGroup>

              <FormGroup>
                <Label htmlFor="phone">Teléfono *</Label>
                <Input
                  id="phone"
                  type="tel"
                  value={formData.phone}
                  onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                  required
                />
              </FormGroup>

              {/* Fila 2: Email | NIF/CIF */}
              <FormGroup>
                <Label htmlFor="email">Email</Label>
                <Input
                  id="email"
                  type="email"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                />
              </FormGroup>

              <FormGroup>
                <Label htmlFor="taxId">NIF/CIF</Label>
                <Input
                  id="taxId"
                  type="text"
                  value={formData.taxId}
                  onChange={(e) => setFormData({ ...formData, taxId: e.target.value })}
                />
              </FormGroup>

              {/* Fila 3: Dirección (full width) */}
              <FullWidth>
                <FormGroup>
                  <Label htmlFor="street">Dirección</Label>
                  <Input
                    id="street"
                    type="text"
                    value={formData.street}
                    onChange={(e) => setFormData({ ...formData, street: e.target.value })}
                  />
                </FormGroup>
              </FullWidth>

              {/* Fila 4: Ciudad | Código Postal */}
              <FormGroup>
                <Label htmlFor="city">Ciudad</Label>
                <Input
                  id="city"
                  type="text"
                  value={formData.city}
                  onChange={(e) => setFormData({ ...formData, city: e.target.value })}
                />
              </FormGroup>

              <FormGroup>
                <Label htmlFor="zip">Código Postal</Label>
                <Input
                  id="zip"
                  type="text"
                  value={formData.zip}
                  onChange={(e) => setFormData({ ...formData, zip: e.target.value })}
                />
              </FormGroup>
              {/* Sin campo de margen: un cliente nuevo nace particular (0); se cambia después en Clientes si hace falta */}

              {/* Espacio vacío para mantener el grid */}
              <div></div>

              {/* Botones (full width) */}
              <FullWidth>
                <ButtonGroup>
                <Button
                  type="button"
                  className="secondary"
                  onClick={() => {
                    setShowModal(false);
                    resetForm();
                  }}
                >
                  Cancelar
                </Button>
                <Button
                  type="submit"
                  className="primary"
                  disabled={createClientMutation.isLoading}
                >
                  {createClientMutation.isLoading ? 'Creando...' : 'Crear Cliente'}
                </Button>
              </ButtonGroup>
              </FullWidth>
            </Form>
          </ModalContent>
        </Modal>
      )}
    </>
  );
};

export default ClientSelectWithCreate;
